const IDENTITY_KEY = '__indiefyChatwootIdentity';
const BASE_URL = 'https://support.indiefy.net';
const WEBSITE_TOKEN = 'SiH7diQpyGfcvRiyxMGSifaq';
const SCRIPT_ATTR = 'data-indiefy-chatwoot';
const APPLY_INTERVAL_MS = 250;
const MAX_APPLY_ATTEMPTS = 80;
const MAX_SDK_LOAD_ATTEMPTS = 5;
const MAX_RECOVERIES = 2;
const SDK_LOAD_TIMEOUT_MS = 15000;

let pendingUser = null;
let listenerBound = false;
let applyTimer = null;
let applyAttempts = 0;
let sdkRetryTimer = null;
let sdkLoadAttempts = 0;
let recoveries = 0;
let recovering = false;
let identityFailures = 0;

function userSignature(user) {
  if (!user || !user._id) {
    return '';
  }

  return [
    String(user._id),
    user.email || '',
    user.name || user.username || '',
    user.subscription ? '1' : '0',
  ].join('|');
}

function getAppliedSignature() {
  return window[IDENTITY_KEY] || '';
}

function setAppliedSignature(signature) {
  window[IDENTITY_KEY] = signature;
}

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('indiefy-user') || 'null');
  } catch (e) {
    return null;
  }
}

function userPayload(user) {
  const email = user.email || '';
  const name = user.name || user.username || '';

  return {
    email,
    name: name || (email ? email.split('@')[0] : 'User'),
  };
}

function isWidgetMounted() {
  return Boolean(
    document.getElementById('cw-widget-holder') ||
    document.getElementById('cw-bubble-holder'),
  );
}

function isWidgetReady() {
  return Boolean(
    window.$chatwoot &&
    window.$chatwoot.hasLoaded &&
    isWidgetMounted(),
  );
}

function stopApplyLoop() {
  if (applyTimer) {
    clearTimeout(applyTimer);
    applyTimer = null;
  }
}

function stopSdkRetry() {
  if (sdkRetryTimer) {
    clearTimeout(sdkRetryTimer);
    sdkRetryTimer = null;
  }
}

/**
 * `$chatwoot` exists as soon as `chatwootSDK.run()` returns, but the
 * iframe has not loaded yet. `setUser` then posts to a dead iframe,
 * writes the identity cookie, and later calls become no-ops. Only
 * identify after `hasLoaded` (the same moment `chatwoot:ready` fires).
 */
function applyIdentity(user) {
  if (!isWidgetReady()) {
    return false;
  }

  if (!user || !user._id) {
    return true;
  }

  const signature = userSignature(user);
  if (signature === getAppliedSignature()) {
    return true;
  }

  try {
    window.$chatwoot.setUser(String(user._id), userPayload(user));

    if (user.subscription) {
      if (window.$chatwoot.setCustomAttributes) {
        window.$chatwoot.setCustomAttributes({ pro: true });
      }
      if (window.$chatwoot.setLabel) {
        window.$chatwoot.setLabel('pro');
      }
    }

    setAppliedSignature(signature);
    return true;
  } catch (error) {
    console.warn('Chatwoot identity failed:', error);
    return false;
  }
}

function scheduleApply() {
  if (applyIdentity(pendingUser)) {
    stopApplyLoop();
    applyAttempts = 0;
    return;
  }

  if (
    window.$chatwoot &&
    !isWidgetMounted() &&
    applyAttempts >= 4
  ) {
    recoverBrokenSdk();
    return;
  }

  if (applyAttempts >= MAX_APPLY_ATTEMPTS) {
    stopApplyLoop();
    if (window.$chatwoot) recoverBrokenSdk();
    return;
  }

  applyAttempts += 1;
  stopApplyLoop();
  applyTimer = setTimeout(scheduleApply, APPLY_INTERVAL_MS);
}

function onChatwootReady() {
  scheduleApply();
}

function onChatwootError() {
  // The SDK writes its setUser fingerprint before the server accepts
  // the identity. An asynchronous failure must allow a bounded retry.
  if (!pendingUser || identityFailures >= 3) return;
  identityFailures += 1;
  setAppliedSignature('');
  stopApplyLoop();
  applyTimer = setTimeout(scheduleApply, 1000 * identityFailures);
}

function bindReadyListener() {
  if (listenerBound || typeof window === 'undefined') {
    return;
  }

  listenerBound = true;
  window.addEventListener('chatwoot:ready', onChatwootReady);
  window.addEventListener('chatwoot:error', onChatwootError);
  window.addEventListener('online', onConnectionRestored);
}

function onConnectionRestored() {
  sdkLoadAttempts = 0;
  recoveries = 0;
  applyAttempts = 0;
  startChatwoot();
}

function existingSdkScript() {
  return document.querySelector(
    `script[${SCRIPT_ATTR}], script[src*="/packs/js/sdk.js"]`,
  );
}

function discardSdk() {
  // Remove the entire dead instance, keeping cw_conversation intact.
  // A cached/prerendered iframe is DOM, not an initialized SDK instance.
  document.querySelectorAll(
    '#cw-widget-holder, #cw-bubble-holder, .woot--bubble-holder, ' +
    '#chatwoot_live_chat_widget',
  ).forEach(element => element.remove());
  window.$chatwoot = undefined;
  window.chatwootSDK = undefined;
  setAppliedSignature('');
  document.querySelectorAll(
    `script[${SCRIPT_ATTR}], script[src*="/packs/js/sdk.js"]`,
  ).forEach((script) => script.remove());
  const styles = document.getElementById('cw-widget-styles');
  if (styles) {
    styles.remove();
  }
}

/**
 * sdk.js captures `document.body` when it evaluates. If that script
 * ran from <head> before body existed, `run()` cannot mount the
 * widget for the rest of the page life. Drop it and load into body.
 */
function recoverBrokenSdk() {
  if (recovering || recoveries >= MAX_RECOVERIES) {
    return;
  }

  recovering = true;
  recoveries += 1;
  stopApplyLoop();
  applyAttempts = 0;
  sdkLoadAttempts = 0;
  discardSdk();
  recovering = false;
  loadSdkScript();
}

function runSdk() {
  if (window.$chatwoot && isWidgetMounted()) {
    return;
  }

  if (window.$chatwoot && !isWidgetMounted()) {
    recoverBrokenSdk();
    return;
  }

  if (!window.chatwootSDK || !window.chatwootSDK.run) {
    return;
  }

  try {
    window.chatwootSDK.run({
      websiteToken: WEBSITE_TOKEN,
      baseUrl: BASE_URL,
    });
  } catch (error) {
    console.warn('Chatwoot run failed, reloading SDK:', error);
    recoverBrokenSdk();
    return;
  }

  if (window.$chatwoot && !isWidgetMounted()) {
    recoverBrokenSdk();
  }
}

function onSdkAvailable() {
  stopSdkRetry();
  runSdk();
  scheduleApply();
}

function loadSdkScript() {
  if (typeof document === 'undefined') {
    return;
  }

  if (!document.body) {
    document.addEventListener(
      'DOMContentLoaded',
      loadSdkScript,
      { once: true },
    );
    return;
  }

  if (!window.$chatwoot && isWidgetMounted()) {
    discardSdk();
  }

  const existing = existingSdkScript();
  if (existing && !document.body.contains(existing)) {
    discardSdk();
  }

  if (window.$chatwoot && !isWidgetMounted()) {
    recoverBrokenSdk();
    return;
  }

  if (window.$chatwoot || window.chatwootSDK) {
    onSdkAvailable();
    return;
  }

  const pending = existingSdkScript();
  if (pending) {
    if (pending.getAttribute('data-indiefy-waiting') !== '1') {
      pending.setAttribute('data-indiefy-waiting', '1');
      pending.addEventListener('load', onSdkAvailable);
      pending.addEventListener('error', retrySdkLoad);
    }
    if (!sdkRetryTimer) {
      sdkRetryTimer = setTimeout(retrySdkLoad, SDK_LOAD_TIMEOUT_MS);
    }
    return;
  }

  sdkLoadAttempts += 1;
  const script = document.createElement('script');
  script.src = `${BASE_URL}/packs/js/sdk.js`;
  script.async = true;
  script.setAttribute(SCRIPT_ATTR, 'true');
  script.onload = onSdkAvailable;
  script.onerror = retrySdkLoad;
  document.body.appendChild(script);
  stopSdkRetry();
  sdkRetryTimer = setTimeout(retrySdkLoad, SDK_LOAD_TIMEOUT_MS);
}

function retrySdkLoad() {
  stopSdkRetry();
  const failed = existingSdkScript();
  if (failed) {
    failed.remove();
  }

  if (sdkLoadAttempts >= MAX_SDK_LOAD_ATTEMPTS) {
    console.warn('Chatwoot SDK failed to load');
    return;
  }

  sdkRetryTimer = setTimeout(
    loadSdkScript, 1000 * Math.max(1, sdkLoadAttempts),
  );
}

/**
 * Boot the widget (or attach to the snippet in index.html) and keep
 * trying to apply the queued user until the iframe is actually ready.
 */
export function startChatwoot() {
  if (typeof window === 'undefined' || window.__INDIEFY_PRERENDER__) {
    return;
  }

  window.chatwootSettings = window.chatwootSettings || {
    position: 'right',
    type: 'standard',
    launcherTitle: '',
  };

  if (!pendingUser) {
    pendingUser = readStoredUser();
  }

  bindReadyListener();
  loadSdkScript();
  scheduleApply();
}

/**
 * Queue Chatwoot identity until the widget iframe has loaded.
 */
export function identifyChatwootUser(user) {
  if (userSignature(user) !== userSignature(pendingUser)) {
    identityFailures = 0;
  }
  pendingUser = user || null;
  applyAttempts = 0;
  startChatwoot();
}

/**
 * Drop the current Chatwoot session (logout / expired session).
 * Do not call this from `chatwoot:ready` — `reset()` reloads the iframe
 * and suppresses further ready events.
 */
export function resetChatwoot() {
  pendingUser = null;
  setAppliedSignature('');
  stopApplyLoop();
  applyAttempts = 0;

  try {
    if (window.$chatwoot && window.$chatwoot.reset) {
      // reset() reloads the frame but the deployed SDK keeps user and
      // hasLoaded. Clear them so it cannot re-identify the logged-out
      // account, or accept a new identity before the new frame loads.
      delete window.$chatwoot.user;
      delete window.$chatwoot.identifier;
      window.$chatwoot.hasLoaded = false;
      window.$chatwoot.reset();
    }
  } catch (error) {
    console.warn('Chatwoot reset failed:', error);
  }
}

export function hydrateChatwootFromStorage() {
  const storedUser = readStoredUser();
  if (storedUser && storedUser._id) {
    identifyChatwootUser(storedUser);
  } else {
    startChatwoot();
  }
}

export function stopChatwootClient() {
  stopApplyLoop();
  stopSdkRetry();
  applyAttempts = 0;
  sdkLoadAttempts = 0;
  recoveries = 0;
  recovering = false;
  pendingUser = null;
  identityFailures = 0;
  listenerBound = false;
  window.removeEventListener('chatwoot:ready', onChatwootReady);
  window.removeEventListener('chatwoot:error', onChatwootError);
  window.removeEventListener('online', onConnectionRestored);
}
